import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Button,
  FilePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@quietbuildlab/ui";
import { CreateDealSchema } from "@bargain-radar/shared";
import { categoriesQueryOptions, createDeal, uploadImage } from "../queries/deals";

export const Route = createFileRoute("/submit")({
  beforeLoad: ({ context, location }) => {
    if (!context.session) {
      throw redirect({ to: "/login", search: { next: location.href } });
    }
  },
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(categoriesQueryOptions);
  },
  component: SubmitPage,
});

function SubmitPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const categories = useQuery(categoriesQueryOptions);

  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [priceDollars, setPriceDollars] = useState("");
  const [originalDollars, setOriginalDollars] = useState("");
  const [merchant, setMerchant] = useState("");
  const [categorySlug, setCategorySlug] = useState("");
  const [description, setDescription] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tagSlugs, setTagSlugs] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: createDeal,
    onSuccess: async ({ id, slug }) => {
      await qc.invalidateQueries({ queryKey: ["feed"] });
      navigate({ to: "/deal/$id/$slug", params: { id, slug } });
    },
    onError: (err) => setFormError((err as Error).message),
  });

  async function handleFiles(files: File[]) {
    // FilePicker fires with [] when the user clears the selection.
    const file = files[0];
    if (!file) {
      setImageUrl(null);
      setUploadError(null);
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setImageUrl(url);
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  function addTag() {
    const slug = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "");
    if (!slug) return;
    if (tagSlugs.includes(slug)) {
      setTagInput("");
      return;
    }
    if (tagSlugs.length >= 5) return;
    setTagSlugs((t) => [...t, slug]);
    setTagInput("");
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const parsePrice = (s: string): number | null => {
      const v = s.trim();
      if (!v) return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) return null;
      return Math.round(n * 100);
    };
    const payload = {
      title: title.trim(),
      url: url.trim(),
      priceCents: parsePrice(priceDollars),
      originalPriceCents: parsePrice(originalDollars),
      currency: "AUD",
      merchant: merchant.trim() || null,
      description,
      imageUrl,
      categorySlug,
      tagSlugs,
    };
    const parsed = CreateDealSchema.safeParse(payload);
    if (!parsed.success) {
      setFormError(parsed.error.issues.map((i) => `${i.path.join(".") || "form"}: ${i.message}`).join("; "));
      return;
    }
    submit.mutate(parsed.data);
  }

  // bargain-radar's input bg override applied to every qbui Input/Textarea/SelectTrigger.
  const inputBg = "bg-[color:var(--color-panel)]";

  return (
    <main className="mx-auto w-full max-w-2xl px-4 sm:px-5 py-6 sm:py-8 flex-1">
      <h1 className="text-2xl font-semibold tracking-tight mb-2">Submit a deal</h1>
      <p className="text-sm text-[color:var(--color-fg-muted)] mb-6">
        AUD by default. Be specific — what, how much, where, and why it’s a deal.
      </p>
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label="Title" required>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="iPhone 15 256GB on sale"
            className={inputBg}
            required
          />
        </Field>
        <Field label="Deal URL" required>
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://retailer.com.au/…"
            className={inputBg}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Price (AUD)">
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={priceDollars}
              onChange={(e) => setPriceDollars(e.target.value)}
              placeholder="1299"
              className={inputBg}
            />
          </Field>
          <Field label="Original price (AUD)">
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={originalDollars}
              onChange={(e) => setOriginalDollars(e.target.value)}
              placeholder="1749"
              className={inputBg}
            />
          </Field>
        </div>
        <Field label="Merchant">
          <Input
            type="text"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            placeholder="JB Hi-Fi"
            className={inputBg}
          />
        </Field>
        <Field label="Category" required>
          <Select value={categorySlug} onValueChange={setCategorySlug} required>
            <SelectTrigger className={`w-full ${inputBg}`}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--color-panel)] border-[color:var(--color-line)]">
              {categories.data?.map((c) => (
                <SelectItem key={c.slug} value={c.slug} className="focus:bg-[color:var(--color-panel-2)]">
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tags (up to 5)">
          <div className="flex gap-2">
            <Input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder="iphone"
              className={`flex-1 ${inputBg}`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addTag}
              className="hover:bg-[color:var(--color-panel-2)]"
            >
              Add
            </Button>
          </div>
          {tagSlugs.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1">
              {tagSlugs.map((t) => (
                <li key={t} className="rounded-full border border-[color:var(--color-line)] px-2 py-0.5 text-xs flex items-center gap-1">
                  #{t}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setTagSlugs((tags) => tags.filter((x) => x !== t))}
                    aria-label={`remove ${t}`}
                    className="h-4 w-4 text-[color:var(--color-fg-muted)] hover:bg-transparent hover:text-[color:var(--color-red)]"
                  >
                    ×
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Field>
        <Field label="Image">
          <FilePicker
            accept="image/jpeg,image/png,image/webp,image/gif"
            disabled={uploading}
            maxSize={10 * 1024 * 1024}
            onFilesChange={handleFiles}
            onError={(reason, file) => {
              setUploadError(
                reason === "file-too-large"
                  ? `${file?.name ?? "Image"} exceeds 10 MB`
                  : reason === "invalid-type"
                  ? `${file?.name ?? "File"} is not a supported image format`
                  : `Upload error: ${reason}`,
              );
            }}
          />
          {uploading && (
            <p className="text-xs text-[color:var(--color-fg-muted)] mt-2">Uploading…</p>
          )}
          {uploadError && (
            <p className="text-xs text-[color:var(--color-red)] mt-2">{uploadError}</p>
          )}
          {imageUrl && !uploading && (
            <p className="text-xs text-[color:var(--color-fg-muted)] mt-2">
              Uploaded ✓{" "}
              <a
                href={imageUrl}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                view
              </a>
            </p>
          )}
        </Field>
        <Field label="Description (Markdown)">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Why is this a good deal? Stack with… in-store / online…"
            className={inputBg}
            maxLength={5000}
          />
        </Field>
        {formError && <p className="text-sm text-[color:var(--color-red)]">{formError}</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending ? "Posting…" : "Post deal"}
          </Button>
        </div>
      </form>
    </main>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">
        {label}
        {required && <span className="text-[color:var(--color-red)] ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
